import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Monitor, Smartphone } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Field, FormError, Input } from "@/components/ui/field";
import { InstallApp } from "@/features/pwa/install";
import type { Schemas } from "@/lib/api/client";
import {
  devicesQuery,
  useChangePassword,
  useRevokeDevice,
  useRevokeOtherDevices,
} from "@/lib/api/queries";
import { describeUserAgent, formatDate, formatRelative, plural } from "@/lib/format";
import { applyApiError } from "@/lib/forms";

export const Route = createFileRoute("/_app/settings")({
  loader: ({ context }) => void context.queryClient.prefetchQuery(devicesQuery),
  component: SettingsPage,
});

const schema = z
  .object({
    current_password: z.string().min(1, "Введите текущий пароль"),
    new_password: z.string().min(8, "Минимум 8 символов").max(128),
    repeat: z.string(),
  })
  .refine((v) => v.new_password === v.repeat, {
    path: ["repeat"],
    message: "Пароли не совпадают",
  });
type Values = z.infer<typeof schema>;

function SettingsPage() {
  const { session } = Route.useRouteContext();
  const change = useChangePassword();
  const [formError, setFormError] = useState<string>();
  const form = useForm<Values>({ resolver: zodResolver(schema) });
  const { errors } = form.formState;

  const submit = form.handleSubmit(async ({ current_password, new_password }) => {
    setFormError(undefined);
    try {
      await change.mutateAsync({ current_password, new_password });
      form.reset();
      toast.success("Пароль изменён, на других устройствах нужно войти заново");
    } catch (error) {
      setFormError(applyApiError(error, form.setError, ["current_password", "new_password"]));
    }
  });

  return (
    <main className="mx-auto max-w-xl px-4 pt-8 pb-16">
      <h1 className="text-xl">Настройки</h1>
      <p className="mt-1 text-sm text-muted">
        {session.user.username}, с нами с {formatDate(session.user.created_at)}
      </p>

      <form onSubmit={submit} className="mt-8 grid gap-4" noValidate>
        <h2 className="text-base">Смена пароля</h2>
        <Field label="Текущий пароль" error={errors.current_password?.message}>
          {(control) => (
            <Input
              {...control}
              {...form.register("current_password")}
              type="password"
              autoComplete="current-password"
            />
          )}
        </Field>
        <Field label="Новый пароль" error={errors.new_password?.message}>
          {(control) => (
            <Input
              {...control}
              {...form.register("new_password")}
              type="password"
              autoComplete="new-password"
            />
          )}
        </Field>
        <Field label="Ещё раз" error={errors.repeat?.message}>
          {(control) => (
            <Input
              {...control}
              {...form.register("repeat")}
              type="password"
              autoComplete="new-password"
            />
          )}
        </Field>
        <FormError message={formError} />
        <Button type="submit" pending={change.isPending} className="justify-self-start">
          Сменить пароль
        </Button>
      </form>

      <Devices />
      <InstallApp />
    </main>
  );
}

type Device = Schemas["ActiveSessionOut"];

function Devices() {
  const { data } = useQuery(devicesQuery);
  const revoke = useRevokeDevice();
  const revokeOthers = useRevokeOtherDevices();
  const [confirming, setConfirming] = useState(false);
  if (!data) return null;
  const others = data.filter((d) => !d.current).length;

  return (
    <section className="mt-12">
      <h2 className="text-base">Где выполнен вход</h2>
      <p className="mt-1 text-sm text-muted">
        Незнакомое устройство — завершите его сессию и смените пароль.
      </p>
      <ul className="mt-3 divide-y divide-line rounded-2xl border border-line bg-surface">
        {data.map((device) => (
          <DeviceRow
            key={device.id}
            device={device}
            pending={revoke.isPending && revoke.variables === device.id}
            onRevoke={() =>
              revoke.mutate(device.id, {
                onSuccess: () => toast.success("Сессия завершена"),
                onError: (error) => toast.error(error.message),
              })
            }
          />
        ))}
      </ul>
      {others > 0 && (
        <Button variant="secondary" className="mt-4" onClick={() => setConfirming(true)}>
          Выйти на всех других
        </Button>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Выйти на всех других устройствах?"
        description={`${others} ${plural(others, ["сессия будет завершена", "сессии будут завершены", "сессий будут завершены"])}. Это устройство останется в аккаунте. VPN-конфиги продолжат работать.`}
        confirmLabel="Выйти"
        danger
        pending={revokeOthers.isPending}
        onConfirm={() =>
          revokeOthers.mutate(undefined, {
            onSuccess: () => {
              toast.success("Другие сессии завершены");
              setConfirming(false);
            },
            onError: (error) => toast.error(error.message),
          })
        }
      >
        {null}
      </ConfirmDialog>
    </section>
  );
}

function DeviceRow({
  device,
  pending,
  onRevoke,
}: {
  device: Device;
  pending: boolean;
  onRevoke: () => void;
}) {
  const mobile = /Mobile|Android|iPhone|iPad/.test(device.user_agent ?? "");
  const Icon = mobile ? Smartphone : Monitor;
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <Icon aria-hidden className="size-5 shrink-0 text-muted" />
      <div className="min-w-0 flex-1">
        <p className="truncate">
          {describeUserAgent(device.user_agent)}
          {device.current && <span className="ml-2 text-sm text-amber-text">это устройство</span>}
        </p>
        <p className="truncate text-sm text-muted">
          {[
            device.ip,
            device.current ? "сейчас" : formatRelative(device.last_used_at),
            `вход ${formatDate(device.created_at)}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      {!device.current && (
        <Button size="sm" variant="ghost" pending={pending} onClick={onRevoke}>
          Завершить
        </Button>
      )}
    </li>
  );
}
